import api, { ApiError } from '@anm/api';
import { CategoryListResponse } from '@anm/api/modules/templates';
import { call, put, select, take, takeLatest } from '@anm/helpers/saga/effects';
import { takeType } from '@anm/helpers/saga/typesafe-actions';

import createNestedListFromStringList from '../../../helpers/createNestedListFromStringList';
import { collapseActions } from '../collapse';
import { filterActions } from '../filter';

import { categoryActions, CATEGORY_LIST_REQUEST } from './actions';
import { selectCategoriesCount, selectCategoryFilter, selectCategoryFromList } from './selectors';
import { CurrentCategoryState } from './types';

const createDescriptionFromName = (name: string) =>
  `Use our online video maker to create ${name} videos for business and social media.`;

export function* updateCurrentCategory() {
  const categoryFilter = yield* select(selectCategoryFilter);
  if (!categoryFilter) {
    yield put(categoryActions.resetCurrentCategory());
    return;
  }

  const categoryItem: ReturnType<ReturnType<typeof selectCategoryFromList>> = yield select(
    selectCategoryFromList(categoryFilter)
  );
  if (!categoryItem) return;

  const currentCategory: CurrentCategoryState = {
    ...categoryItem,
    description: createDescriptionFromName(categoryItem.name)
  };

  yield put(categoryActions.updateCurrentCategory(currentCategory));
  yield call(extendByCategoryFilter);
}

export function* extendByCategoryFilter() {
  const categoryFilter = yield* select(selectCategoryFilter);
  yield categoryFilter && put(collapseActions.extendItem(categoryFilter));
}

function* fetchCategoryList() {
  try {
    yield take(CATEGORY_LIST_REQUEST);

    const categoriesCount = yield* select(selectCategoriesCount);
    const isCategoriesLoaded = categoriesCount > 0;
    if (isCategoriesLoaded) return;

    const { content: categoryList, count }: CategoryListResponse = yield call(api().templates.getCategories, {
      limit: 500,
      offset: 0
    });

    const strList = categoryList.map(({ name }) => name.substr(1)); // subsrt - remove first '/'
    const nestedList = createNestedListFromStringList(strList);

    yield put(categoryActions.getCategoryListSuccess(nestedList, count));
    yield call(updateCurrentCategory);
    return nestedList;
  } catch (e) {
    yield put(categoryActions.getCategoryListError(e as ApiError));
  }
}

const categoryWatchers = () => [
  call(fetchCategoryList),
  takeLatest(takeType(filterActions.update), updateCurrentCategory)
];

export default categoryWatchers;
